/**
 * DOOSP program catalog — single source of truth for all certification programs.
 * The Bloodborne Pathogens program is the flagship, fully live (linked to the existing LMS).
 * Others are informational pages with full curricula scoped and authored; enrollment opens after launch.
 */

export const PROGRAMS = [
  {
    slug: "bloodborne-pathogens",
    name: "Bloodborne Pathogens Training",
    shortName: "BBP Training",
    status: "live", // "live" | "coming-soon"
    tagline: "OSHA 29 CFR 1910.1030 · profession-tailored AI case study",
    summary:
      "Our flagship program. Complete 13 interactive modules, pass a 50-MCQ exam with an AI-graded case study, and earn an independent competency-based IMCAC certificate valid for 2 years.",
    icon: "ShieldAlert",
    color: "red",
    heroImage:
      "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=1600&q=80&auto=format&fit=crop",
    modules: 13,
    durationLabel: "45–60 min",
    priceUsd: 30,
    ctaLabel: "Start Certification",
    ctaHref: "/",
    lmsHref: "/course",
    highlights: [
      "OSHA-aligned lessons on HBV/HCV/HIV, transmission, PPE, universal precautions",
      "50-MCQ exam with a Claude-graded AI case study tailored to your profession",
      "Downloadable PDF certificate with QR verification, valid 2 years",
      "Module 13 — unlimited AI practice cases for your specialty",
    ],
    audiences: [
      "Nurses, EMTs, phlebotomists, lab technicians",
      "Custodial and housekeeping staff in healthcare",
      "Tattoo artists, body piercers, cosmetologists",
      "First responders, school nurses, firefighters",
    ],
    standards: ["OSHA 29 CFR 1910.1030", "CDC Universal Precautions", "NIOSH sharps guidance"],
    prerequisites: "None. Suitable for first-time learners and annual re-certification.",
    assessment: "50-question randomized MCQ (70% of grade) + Claude Sonnet 4.5-graded profession-tailored case study (30% of grade). Passing threshold: 80% overall.",
    learningObjectives: [
      "Identify bloodborne pathogens of concern (HBV, HCV, HIV) and their transmission routes.",
      "Apply universal precautions and OSHA-mandated engineering controls, work practices, and PPE.",
      "Execute the correct post-exposure protocol including source-patient consent, PEP timelines, and documentation.",
      "Interpret and follow your organization's Exposure Control Plan.",
    ],
  },
  {
    slug: "infection-prevention-control",
    name: "Infection Prevention & Control",
    shortName: "IPC",
    status: "coming-soon",
    tagline: "Chain of infection, standard & transmission-based precautions",
    summary:
      "A comprehensive program covering hand hygiene, environmental cleaning, sterilization, aseptic technique, and outbreak response for healthcare and long-term care settings.",
    icon: "ShieldCheck",
    color: "emerald",
    heroImage:
      "https://images.unsplash.com/photo-1584515933487-779824d29309?w=1600&q=80&auto=format&fit=crop",
    modules: 12,
    durationLabel: "60–90 min",
    priceUsd: 45,
    ctaLabel: "Join the waitlist",
    ctaHref: "/contact?program=ipc",
    highlights: [
      "CDC and WHO IPC guidelines with case-based learning",
      "Isolation precautions (contact / droplet / airborne)",
      "Sterilization & high-level disinfection protocols",
      "Outbreak identification, reporting, and containment",
    ],
    audiences: [
      "Infection preventionists and safety officers",
      "Nursing staff and clinical educators",
      "Dental clinic and outpatient center staff",
      "Long-term care and hospice workers",
    ],
    standards: [
      "CDC HICPAC Guideline for Isolation Precautions (2007, updated)",
      "WHO Guidelines on Core Components of IPC (2016)",
      "APIC Text of Infection Control and Epidemiology",
      "The Joint Commission — National Patient Safety Goals: HAI reduction",
    ],
    prerequisites: "Basic clinical background helpful but not required. Suitable for new IP designees and clinical staff seeking IPC competency.",
    assessment: "60-MCQ knowledge exam + a scenario-based outbreak investigation exercise reviewed by faculty. Passing threshold: 80%.",
    learningObjectives: [
      "Describe the chain of infection and identify break points for each transmission mode.",
      "Apply CDC-tiered precautions (Standard, Contact, Droplet, Airborne) to specific pathogens.",
      "Distinguish between cleaning, low/intermediate/high-level disinfection, and sterilization.",
      "Recognize the early signs of an outbreak and initiate the containment cascade.",
      "Author or interpret an IPC risk assessment for your care setting.",
    ],
    curriculum: [
      { n: 1, title: "The chain of infection", body: "Reservoir, portal of exit, mode of transmission, portal of entry, susceptible host — mapped to real clinical exposures." },
      { n: 2, title: "Standard Precautions", body: "The foundation of IPC. Hand hygiene, PPE, respiratory etiquette, safe injection practices, and patient placement." },
      { n: 3, title: "Contact, Droplet & Airborne precautions", body: "Transmission-based precautions with pathogen-specific examples: MRSA, C. difficile, influenza, TB, measles, SARS-CoV-2." },
      { n: 4, title: "Hand hygiene — evidence and audit", body: "WHO 5 Moments, ABHR vs soap, audit methodologies (direct observation, product volume, electronic monitoring)." },
      { n: 5, title: "Environmental cleaning & disinfection", body: "Contact times, EPA List N, high-touch surface prioritization, and role of environmental services." },
      { n: 6, title: "Sterilization & high-level disinfection", body: "Spaulding classification (critical, semi-critical, non-critical). Autoclave cycles, chemical sterilants, endoscope reprocessing." },
      { n: 7, title: "Aseptic technique", body: "Sterile field creation, no-touch technique, dressing changes, IV line and catheter insertion bundles." },
      { n: 8, title: "PPE selection & sequencing", body: "Donning and doffing order to prevent self-contamination. Respirator selection (N95, PAPR) and fit testing." },
      { n: 9, title: "Multi-drug resistant organisms (MDROs)", body: "MRSA, VRE, CRE, ESBL — colonization vs infection, decolonization protocols, contact isolation practices." },
      { n: 10, title: "Outbreak recognition & response", body: "Baseline surveillance, thresholds for action, line lists, epi curves, environmental sampling, communication tree." },
      { n: 11, title: "HAI surveillance & reporting", body: "NHSN definitions for CLABSI, CAUTI, SSI, VAE, and MDRO/CDI. State and CMS reporting requirements." },
      { n: 12, title: "IPC risk assessment & program design", body: "How to conduct an annual IPC risk assessment, prioritize interventions, and structure a facility IPC program." },
    ],
  },
  {
    slug: "healthcare-worker-safety",
    name: "Healthcare Worker Safety",
    shortName: "HCW Safety",
    status: "coming-soon",
    tagline: "Sharps injury prevention, ergonomics, workplace violence",
    summary:
      "Protect the caregiver. Covers safe patient handling, needlestick prevention, ergonomics, workplace violence de-escalation, and mental-health first aid for clinical teams.",
    icon: "HeartPulse",
    color: "amber",
    heroImage:
      "https://images.unsplash.com/photo-1584467735867-4297ae2ebcee?w=1600&q=80&auto=format&fit=crop",
    modules: 10,
    durationLabel: "60 min",
    priceUsd: 40,
    ctaLabel: "Join the waitlist",
    ctaHref: "/contact?program=hcw",
    highlights: [
      "Safe patient handling & mobility (SPHM) techniques",
      "Sharps injury prevention & PEP timelines",
      "Workplace violence prevention and de-escalation",
      "Burnout, fatigue, and mental-health resilience",
    ],
    audiences: [
      "Bedside nurses, PCTs, and CNAs",
      "Emergency department teams",
      "Home health and community health workers",
      "Occupational health managers",
    ],
    standards: [
      "OSHA Needlestick Safety and Prevention Act (2000)",
      "OSHA Guidelines for Preventing Workplace Violence in Healthcare (2015)",
      "ANA Safe Patient Handling & Mobility Interprofessional National Standards",
      "NIOSH Total Worker Health framework",
    ],
    prerequisites: "Open to all healthcare workers. Especially recommended after your first 6 months in a direct-care role.",
    assessment: "50-MCQ exam + reflective safety-review case study on your own workplace. Passing threshold: 80%.",
    learningObjectives: [
      "Identify the top three occupational hazards in your care setting and their evidence-based mitigations.",
      "Apply Safe Patient Handling & Mobility (SPHM) technology and technique.",
      "Execute the correct response to a sharps injury within OSHA-mandated timelines.",
      "Recognize the escalation ladder of workplace violence and apply de-escalation techniques.",
      "Build a personal fatigue-management and burnout-prevention plan.",
    ],
    curriculum: [
      { n: 1, title: "The healthcare-worker safety landscape", body: "OSHA injury statistics, cost of clinician harm, and the ethical case for a Just Culture safety program." },
      { n: 2, title: "Sharps injury prevention", body: "Engineering controls (safety-engineered devices, sharps containers), work practice controls, and the SEIPS incident reporting system." },
      { n: 3, title: "Post-exposure protocol & PEP timelines", body: "First-aid steps, source-patient testing consent, HBV/HCV/HIV PEP timelines, and follow-up serology schedule." },
      { n: 4, title: "Safe Patient Handling & Mobility (SPHM)", body: "Mobility algorithms, mechanical lifts, friction-reducing devices, and staffing-model implications." },
      { n: 5, title: "Ergonomics for clinical work", body: "Workstation setup for EHR-heavy roles, neutral-posture bedside positioning, and micro-break protocols." },
      { n: 6, title: "Workplace violence — Type II & Type III", body: "Patient/visitor violence and lateral (colleague) violence. Warning signs, triage, and organizational responsibilities." },
      { n: 7, title: "De-escalation techniques", body: "Verbal de-escalation frameworks (CPI, PMDB), personal safety positioning, and when to call for help." },
      { n: 8, title: "Fatigue, shiftwork & alertness", body: "Circadian science of night shift, controlled napping, caffeine timing, and the drive-home crash risk." },
      { n: 9, title: "Burnout, moral injury & psychological safety", body: "Diagnostic markers, protective factors, peer-support programs, and evidence-based interventions." },
      { n: 10, title: "Building your personal safety plan", body: "A 4-part personal plan you leave the course with: mitigations, escalation, recovery, and disclosure." },
    ],
  },
  {
    slug: "laser-energy-device-safety",
    name: "Laser & Energy Device Safety",
    shortName: "Laser Safety",
    status: "coming-soon",
    tagline: "ANSI Z136.3 · surgical laser & energy-based device protocols",
    summary:
      "For clinical teams operating surgical lasers, electrosurgery, and radiofrequency devices. Covers ANSI Z136.3 medical laser safety, plume management, and OR fire prevention.",
    icon: "Flame",
    color: "purple",
    heroImage:
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600&q=80&auto=format&fit=crop",
    modules: 8,
    durationLabel: "45 min",
    priceUsd: 55,
    ctaLabel: "Join the waitlist",
    ctaHref: "/contact?program=laser",
    highlights: [
      "ANSI Z136.3 medical laser classification and hazards",
      "Ocular & skin protection, protective eyewear selection",
      "Surgical smoke / plume evacuation and OR fire safety",
      "Laser safety officer (LSO) responsibilities & documentation",
    ],
    audiences: [
      "Perioperative RNs and surgical technologists",
      "Dermatology and aesthetic clinic staff",
      "Ophthalmology and physical therapy teams",
      "Designated laser safety officers",
    ],
    standards: [
      "ANSI Z136.3 — Safe Use of Lasers in Health Care",
      "AORN Guidelines for Laser Safety (annual update)",
      "OSHA Technical Manual — Section III, Chapter 6 (Laser Hazards)",
      "NFPA 99 — Health Care Facilities Code, OR fire safety",
    ],
    prerequisites: "Working knowledge of a clinical or perioperative environment. Recommended for anyone in the room when a laser is fired.",
    assessment: "40-MCQ knowledge exam + a device-specific practical checklist signed off by an on-site LSO or preceptor.",
    learningObjectives: [
      "Classify medical lasers by hazard class and identify the associated MPE and NHZ.",
      "Select appropriate protective eyewear for the laser wavelength in use.",
      "Set up a safe laser use zone with signage, controlled access, and evacuation protocols.",
      "Manage surgical plume with high-efficiency smoke evacuation.",
      "Recognize the three-legged OR fire triangle and execute the fire response plan.",
    ],
    curriculum: [
      { n: 1, title: "Laser physics for clinicians", body: "Wavelength, coherence, power density, and how each affects tissue interaction. No calculus required." },
      { n: 2, title: "ANSI Z136.3 classification & hazards", body: "Class 1 through 4 lasers, Maximum Permissible Exposure (MPE), Nominal Hazard Zone (NHZ)." },
      { n: 3, title: "Ocular safety & eyewear selection", body: "Wavelength-matched OD ratings, side shields, and how to check that goggles are compatible with the laser in use today." },
      { n: 4, title: "Skin & tissue protection", body: "Non-reflective drapes, wet-towel margins, hand-piece hygiene, and prevention of collateral tissue damage." },
      { n: 5, title: "Surgical smoke / plume", body: "Cytotoxic and infectious components of plume, evacuation flow rates, ULPA filter change intervals, and OSHA/AORN recommendations." },
      { n: 6, title: "OR fire triangle & response", body: "Ignition source, fuel, oxidizer. Alcohol-based prep drying times, oxygen flow management, and the response cascade if fire starts." },
      { n: 7, title: "Laser Safety Officer (LSO) role", body: "Responsibilities, credentialing, hazard analysis reports, incident reporting, and annual audits." },
      { n: 8, title: "Case reviews & documentation", body: "Three real anonymized incident reviews (retinal injury, plume exposure, drape fire) and the required documentation trail." },
    ],
  },
  {
    slug: "clinical-waste-management",
    name: "Clinical Waste Management",
    shortName: "Waste Management",
    status: "coming-soon",
    tagline: "Biomedical waste segregation, transport & disposal",
    summary:
      "Comprehensive training on regulated medical waste — segregation at source, labelling, packaging, transport, treatment, and audit trails aligned with EPA/RCRA and WHO guidance.",
    icon: "Biohazard",
    color: "orange",
    heroImage:
      "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1600&q=80&auto=format&fit=crop",
    modules: 9,
    durationLabel: "60 min",
    priceUsd: 40,
    ctaLabel: "Join the waitlist",
    ctaHref: "/contact?program=waste",
    highlights: [
      "Color-coded segregation of infectious, pathological & pharmaceutical waste",
      "Sharps handling, storage & manifest tracking",
      "Autoclaving, incineration & alternative treatment methods",
      "Regulatory compliance: EPA, DOT, RCRA & WHO frameworks",
    ],
    audiences: [
      "Environmental services & housekeeping supervisors",
      "Biosafety and compliance officers",
      "Pharmacy and lab waste custodians",
      "Facilities managers in healthcare",
    ],
    standards: [
      "EPA Medical Waste Tracking Act & state programs",
      "DOT 49 CFR — Hazardous Materials Regulations",
      "RCRA Hazardous Waste rules (for pharmaceutical waste)",
      "WHO Safe Management of Waste from Health-Care Activities (Blue Book)",
    ],
    prerequisites: "None. Recommended for anyone who generates, transports, or oversees medical waste — from bedside caregivers to facility directors.",
    assessment: "40-MCQ exam + a segregation simulation using a mixed-waste tote. Passing threshold: 80%.",
    learningObjectives: [
      "Classify waste correctly at the point of generation.",
      "Apply state and federal storage-time limits and container specifications.",
      "Complete a DOT-compliant manifest for regulated medical waste shipments.",
      "Compare autoclaving, incineration, chemical, and microwave treatment on efficacy and environmental impact.",
      "Design an internal audit checklist for your facility's waste stream.",
    ],
    curriculum: [
      { n: 1, title: "The regulated medical waste landscape", body: "How EPA, DOT, OSHA, and state regulators divide oversight, and what changes when your waste crosses state lines." },
      { n: 2, title: "Waste categorization at the source", body: "Infectious/biohazardous, pathological, sharps, chemotherapeutic, pharmaceutical (hazardous vs non-hazardous), radioactive, and general trash." },
      { n: 3, title: "Color-coded segregation systems", body: "Red bag / yellow bag / black bag conventions internationally, and how mis-segregation drives cost and risk." },
      { n: 4, title: "Sharps management", body: "Container specifications, fill lines, replacement schedules, and safe replacement procedures." },
      { n: 5, title: "Pharmaceutical waste", body: "DEA controlled-substance disposal, RCRA hazardous drug list, and non-hazardous drug pathways." },
      { n: 6, title: "Storage, packaging & labelling", body: "Time and temperature limits, secondary containment, biohazard labelling, and DOT shipping-name requirements." },
      { n: 7, title: "Transport & manifests", body: "Chain-of-custody, cradle-to-grave manifest tracking, generator vs transporter responsibilities, and reject-load procedures." },
      { n: 8, title: "Treatment technologies", body: "Autoclave validation, incinerator emissions, microwave, and chemical treatment. Efficacy vs environmental trade-offs." },
      { n: 9, title: "Auditing your waste program", body: "Building an internal audit — waste characterization studies, spot-check protocols, and cost-reduction opportunities." },
    ],
  },
];

export const getProgram = (slug) => PROGRAMS.find((p) => p.slug === slug);

export const DOOSP_INFO = {
  name: "Department of Occupational Safety Programs",
  short: "DOOSP",
  tagline: "Advancing Workplace Health & Safety through independent competency assessment",
  contactEmail: "admin@imcac.org",
  parent: "IMCAC",
  parentFull: "International Medical Competency Accreditation Council",
};
